// Templates Marketplace — install workflows in 1 click
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Download, Clock, TrendingUp, Search } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const CATEGORIES = ["Tous", "Comptabilité", "Marketing", "E-commerce", "RH", "Commercial", "Direction"];

export default function Marketplace() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(null);
  const [cat, setCat] = useState("Tous");
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).finally(() => setLoading(false));
  }, []);

  const install = async (slug) => {
    setInstalling(slug);
    try {
      const { data } = await api.post(`/templates/${slug}/install`);
      toast.success(`Template installé : ${data.name}`);
      navigate(`/app/workflows/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Installation échouée");
    } finally {
      setInstalling(null);
    }
  };

  const filtered = templates.filter((t) => {
    if (cat !== "Tous" && t.category !== cat) return false;
    if (q && !(t.name + t.description).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="marketplace-page">
      <div className="mb-10">
        <span className="overline">Templates marketplace</span>
        <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">Démarrez en 1 clic.</h1>
        <p className="text-zinc-600 mt-2">Workflows prêts à l'emploi, conçus pour les PME européennes. Installation instantanée dans votre compte.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-8">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un template..."
            className="rounded-sm pl-9"
            data-testid="marketplace-search"
          />
        </div>
        <div className="flex items-center gap-1 overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 py-1.5 text-xs rounded-sm whitespace-nowrap transition-colors ${
                cat === c ? "bg-black text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-zinc-400"
              }`}
              data-testid={`marketplace-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 p-12 text-center">Chargement...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((t, idx) => (
            <motion.div
              key={t.slug}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              whileHover={{ y: -3 }}
              className="bg-white border border-zinc-200 rounded-sm p-6 flex flex-col transition-all"
              data-testid={`template-card-${t.slug}`}
            >
              <div className="flex items-start justify-between mb-4">
                <Badge className="bg-black text-white rounded-sm font-mono text-[10px]">{t.category.toUpperCase()}</Badge>
                <span className="text-[11px] font-mono text-zinc-500">{t.popularity} installs</span>
              </div>
              <h3 className="font-display text-lg font-medium tracking-tight mb-2">{t.name}</h3>
              <p className="text-sm text-zinc-600 mb-5 leading-relaxed flex-1">{t.description}</p>

              <div className="grid grid-cols-2 gap-3 mb-5 pt-4 border-t border-zinc-100">
                <div>
                  <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mb-0.5">Étapes</p>
                  <p className="text-sm font-medium">{t.steps?.length || 0}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mb-0.5">Temps économisé</p>
                  <p className="text-sm font-medium klein-text">{t.estimated_time_saved_minutes} min</p>
                </div>
              </div>

              <Button
                onClick={() => install(t.slug)}
                disabled={installing === t.slug}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 w-full"
                data-testid={`template-install-${t.slug}`}
              >
                {installing === t.slug ? "Installation..." : (<><Download size={14} className="mr-2"/> Installer en 1 clic</>)}
              </Button>
            </motion.div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-3 text-center py-16 text-sm text-zinc-500">
              <Sparkles size={32} strokeWidth={1.5} className="mx-auto mb-3 text-zinc-300"/>
              Aucun template trouvé pour ces critères.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
